import { useSelector } from 'react-redux';

import { type RootState } from '../rootReducer';
import { getImpersonatedCognitoState } from '../utils/Impersonation/impersonation';

export type CognitoUserData = {
    groups: string[];
    id: string;
};
export type CognitoSignedUserState = {
    groups: string[];
    id: string;
    isAdmin: boolean;
    dataIsStale: boolean;
};

const LOCAL_STORAGE_KEY = 'alol_user_cognito';
export const COGNITO_USER_GROUP_ADMIN = 'adm';
export const COGNITO_USER_GROUP_TLB_USER = 'usr';
export const COGNITO_USER_GROUP_TECH = 'tch';
export const COGNITO_USER_GROUP_SALES = 'sls';
export const COGNITO_USER_GROUP_ATO = 'ato';
export const COGNITO_USER_GROUP_OPS = 'ops';
export const COGNITO_USER_GROUP_FLB = 'flb';
export const COGNITO_USER_GROUP_FOM = 'fom';
export const COGNITO_USER_GROUP_CAMO = 'camo';

export const COGNITO_USER_GROUP_REPORT = 'rpt';
export const COGNITO_USER_GROUP_ACCOUNTANT = 'act';

export const COGNITO_USER_STATE_SIGNED_OUT = 'signed-out';
export const COGNITO_USER_STATE_PROCESSING = 'processing';

type CognitoUnsignedUserState = typeof COGNITO_USER_STATE_SIGNED_OUT;
type CognitoProcessingUserState = typeof COGNITO_USER_STATE_PROCESSING;

export type CognitoUserState = CognitoProcessingUserState | CognitoSignedUserState | CognitoUnsignedUserState;

export const cognitoUserInitialState = getInitialState();

export const COGNITO_USER_SIGN_IN = 'COGNITO_USER_SIGN_IN';
export const COGNITO_USER_SIGN_PROCESS_START = 'COGNITO_USER_SIGN_PROCESS_START';
export const COGNITO_USER_SIGN_OUT = 'COGNITO_USER_SIGN_OUT';
export const COGNITO_USER_AUTH_ERROR = 'COGNITO_USER_AUTH_ERROR';
export type CognitoUserActionTypes =
    | {
          type: typeof COGNITO_USER_AUTH_ERROR | typeof COGNITO_USER_SIGN_OUT | typeof COGNITO_USER_SIGN_PROCESS_START;
      }
    | {
          type: typeof COGNITO_USER_SIGN_IN;
          data: CognitoUserData;
      };

export const CognitoUserReducer = (
    state: CognitoUserState | undefined,
    action: CognitoUserActionTypes | any
): CognitoUserState => {
    switch (action.type) {
        case COGNITO_USER_AUTH_ERROR: {
            if (state && typeof state === 'object') {
                return state;
            }
            return COGNITO_USER_STATE_SIGNED_OUT;
        }
        case COGNITO_USER_SIGN_IN: {
            const data = action.data as CognitoSignedUserState;
            persistState(data);
            return {
                groups: [...data.groups],
                id: data.id,
                isAdmin: data.groups.includes(COGNITO_USER_GROUP_ADMIN),
                dataIsStale: false,
            };
        }
        case COGNITO_USER_SIGN_OUT: {
            clearState();
            return COGNITO_USER_STATE_SIGNED_OUT;
        }

        case COGNITO_USER_SIGN_PROCESS_START: {
            if (state && state !== COGNITO_USER_STATE_SIGNED_OUT) {
                return state;
            }
            return COGNITO_USER_STATE_PROCESSING;
        }

        default: {
            return state || getInitialState();
        }
    }
};

export const useCognitoSignedUserState = (): CognitoSignedUserState => {
    const state = useCognitoAnyUserState();
    if (!cognitoUserIsSignedIn(state)) {
        throw new Error('User is not signed in');
    }
    return state;
};

export function cognitoUserIsSignedIn(cognitoState: CognitoUserState): cognitoState is CognitoSignedUserState {
    return cognitoState && typeof cognitoState !== 'string';
}

// While an admin impersonates somebody (see utils/Impersonation), the whole app is served the
// impersonated identity. The real state still has to be signed in - the override never creates
// a session on its own, it only relabels an existing one.
export const useCognitoAnyUserState = (): CognitoUserState => {
    const realState = useRealCognitoAnyUserState();
    const impersonated = getImpersonatedCognitoState();
    if (impersonated && cognitoUserIsSignedIn(realState)) {
        return impersonated;
    }

    return realState;
};

// The identity the user actually signed in with, ignoring any impersonation.
export const useRealCognitoAnyUserState = (): CognitoUserState => {
    return useSelector<RootState, CognitoUserState>((state) => state.cognitoUser);
};

export const isUserInRole = (user: CognitoSignedUserState, role: string[]): boolean => {
    return user.groups.some((g) => role.includes(g));
};

export const canEditMaintenance = (user: CognitoSignedUserState): boolean =>
    user.isAdmin || user.groups.includes(COGNITO_USER_GROUP_CAMO);

function persistState(state: CognitoSignedUserState) {
    window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(state));
}

function clearState() {
    window.localStorage.removeItem(LOCAL_STORAGE_KEY);
}

function getInitialState(): CognitoUserState {
    try {
        const maybeLocalStorageData = window.localStorage.getItem(LOCAL_STORAGE_KEY);
        if (maybeLocalStorageData) {
            const data: CognitoSignedUserState = JSON.parse(maybeLocalStorageData);
            return {
                groups: [...data.groups],
                id: data.id,
                isAdmin: data.groups.includes(COGNITO_USER_GROUP_ADMIN),
                dataIsStale: true,
            };
        }
        return COGNITO_USER_STATE_PROCESSING;
    } catch (e) {
        console.error('Error reloading Cognito User State', e);
        return COGNITO_USER_STATE_PROCESSING;
    }
}
